/**
 * Єдина точка HTTP. Feature-код не викликає fetch, не будує URL
 * і не ставить заголовків — тільки типізовані функції з endpoints/ (§5.2).
 *
 * Ходимо ВИКЛЮЧНО у власний бекенд (apps/api). До провайдера аукціонних даних
 * фронт не звертається ніколи: `apps/web` — статичний бандл, ключа в ньому
 * бути не може за визначенням.
 *
 * У dev Vite проксює /api на apps/api (див. vite.config.ts), тому шлях
 * відносний і CORS у розробці не задіяний.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
		/**
		 * Машинний код помилки з тіла відповіді, якщо бекенд його дав.
		 * Гілки в UI будуються на ньому, а не на тексті: текст може змінитись
		 * у будь-якому релізі бібліотеки автентифікації, код — ні.
		 */
		readonly code?: string,
		/**
		 * Решта полів тіла помилки, крім message/code/error/statusCode: наприклад
		 * суми при INSUFFICIENT_DEPOSIT. Форма залежить від коду, тому unknown.
		 */
		readonly details: Record<string, unknown> = {},
	) {
		super(message);
		this.name = 'ApiError';
	}
}

/**
 * Бекенд віддає помилку як `{ message, error, statusCode }` (Nest) або
 * `{ message, code }` (Better Auth). Дістаємо обидва поля за один розбір
 * тіла — прочитати його вдруге вже не вийде.
 */
async function errorFrom(
	res: Response,
	path: string,
): Promise<{ message: string; code?: string; details: Record<string, unknown> }> {
	let body: unknown = null;
	try {
		body = await res.json();
	} catch {
		// Тіло не JSON — падати тут не можна, помилка вже сталася.
	}

	const { message, code, error: _e, statusCode: _s, ...details } = (body ?? {}) as {
		message?: unknown;
		code?: unknown;
		error?: unknown;
		statusCode?: unknown;
		[key: string]: unknown;
	};
	const text =
		typeof message === 'string' && message.length > 0
			? message
			: Array.isArray(message) && message.length > 0
				? message.join('; ')
				: `Request failed: ${path}`;

	return { message: text, code: typeof code === 'string' ? code : undefined, details };
}

/** Значення параметра запиту. Масив іде повторюваним ключем: `?fuel=A&fuel=B`. */
type QueryValue = string | number | boolean | undefined | readonly string[];

export async function request<T>(
	path: string,
	init: RequestInit & { query?: Record<string, QueryValue> } = {},
): Promise<T> {
	const { query, ...rest } = init;

	const params = new URLSearchParams();
	for (const [k, v] of Object.entries(query ?? {})) {
		// typeof, а не Array.isArray: той звужує readonly string[] до any[].
		if (typeof v === 'object') {
			for (const item of v) params.append(k, item);
		} else if (v !== undefined && v !== '') {
			params.set(k, String(v));
		}
	}
	const qs = params.size > 0 ? `?${params}` : '';

	const res = await fetch(`${BASE_URL}${path}${qs}`, {
		...rest,
		// Сесія їде в httpOnly-куці, а не в заголовку: у localStorage токен
		// доступний будь-якому скрипту на сторінці. `include`, а не
		// `same-origin`, щоб куки працювали й тоді, коли фронт і бекенд
		// стоять на різних доменах.
		credentials: 'include',
		headers: { Accept: 'application/json', ...rest.headers },
	});

	if (!res.ok) {
		const { message, code, details } = await errorFrom(res, path);
		throw new ApiError(message, res.status, code, details);
	}

	/*
	  Порожнє тіло — це валідна відповідь, а не збій. Nest на `return null`
	  віддає 200 із Content-Length: 0, і `res.json()` на ньому кидає
	  «Unexpected end of JSON input». Без цієї гілки запит сесії гостя падав
	  би винятком щоразу — і мовчки, бо результат усе одно виглядав би як
	  «не автентифікований».
	*/
	const body = await res.text();
	if (body.length === 0) return null as T;

	return JSON.parse(body) as T;
}

/**
 * multipart/form-data. Окремо від request(): заголовок Content-Type тут
 * ставить браузер разом із boundary, і задавати його вручну не можна.
 */
export async function upload<T>(path: string, form: FormData): Promise<T> {
	const res = await fetch(`${BASE_URL}${path}`, {
		method: 'POST',
		credentials: 'include',
		headers: { Accept: 'application/json' },
		body: form,
	});
	if (!res.ok) {
		const { message, code, details } = await errorFrom(res, path);
		throw new ApiError(message, res.status, code, details);
	}
	return (await res.json()) as T;
}
